import { Box, Button, Tooltip as NewTooltip, Pill } from '@timelyapp/react';
import classNames from 'classnames';
import CurrencyFormat from 'components/CurrencyFormat';
import InputDecorator from 'components/InputDecorator';
import NumberInput from 'components/NumberInput';
import ProjectBadge from 'components/projects/ProjectBadge';
import Tooltip from 'components/Tooltip';
import { RATE_TYPES } from 'constants/project';
import React, { PureComponent } from 'react';
import FontAwesome from 'react-fontawesome';
import type { Currency } from 'types/account';
import type { Team } from 'types/team';
import type { EnrichedUsersDraftProject } from 'types/users';
import { prefixHexColor } from 'utils/colors';
import { getProjectColor } from 'utils/project';
import { pluralize } from 'utils/text-helper';
import styles from './UserEditorProject.module.scss';

interface Props {
  className?: string;
  permission: EnrichedUsersDraftProject;
  currency: Currency;
  onChange: (updatedProject: { project_id: number; hour_rate: number }) => void;
  onRemove: (projectId: number) => void;
  onFocus: (projectId: number) => void;
  onClickDisabledFeature?: () => void;
  isHourlyRateFeatureEnabled?: boolean;
  teamAssociationsByProjectId?: Record<number, Array<Team>>;
}

class UserEditorProject extends PureComponent<Props> {
  static defaultProps = {
    isHourlyRateFeatureEnabled: true,
  };

  handleRemove = () => {
    this.props.onRemove(this.props.permission.projectId);
  };

  handleFocus = () => {
    this.props.onFocus(this.props.permission.projectId);
  };

  handleDisabledFeatureClickCheck = () => {
    if (!this.props.isHourlyRateFeatureEnabled && this.props.onClickDisabledFeature) {
      this.props.onClickDisabledFeature();
    }
  };

  handleChangeRate = (value: number) => {
    this.props.onChange({
      project_id: this.props.permission.projectId,
      hour_rate: value,
    });
  };

  renderUserRate() {
    const { permission, currency, isHourlyRateFeatureEnabled } = this.props;
    const { draftRate: hourRate, rateOverride } = permission;
    const isDisabled = !isHourlyRateFeatureEnabled;

    if (rateOverride != null) {
      return (
        <>
          <CurrencyFormat
            animated={false}
            value={hourRate ?? null}
            style={{ textDecoration: 'line-through' }}
          />
          <CurrencyFormat animated={false} value={rateOverride} />
        </>
      );
    }

    return (
      <InputDecorator
        onClick={this.handleDisabledFeatureClickCheck}
        isLockedFeature={!isHourlyRateFeatureEnabled}
        size="small"
        prefix={currency.symbol}
      >
        <NumberInput
          size="small"
          disabled={isDisabled}
          name="hour_rate"
          value={hourRate}
          min={0}
          onChange={this.handleChangeRate}
          onFocus={this.handleFocus}
        />
      </InputDecorator>
    );
  }

  renderFixedRate() {
    const { currency, permission } = this.props;

    return (
      <CurrencyFormat animated={false} value={permission.draftRate ?? null} currency={currency} />
    );
  }

  render() {
    const { permission, className, teamAssociationsByProjectId } = this.props;
    const assignedTeams = teamAssociationsByProjectId?.[permission.projectId] ?? [];
    return (
      <div className={classNames(styles.container, className)}>
        <ProjectBadge
          className={styles.badge}
          size="large"
          icon
          color={prefixHexColor(getProjectColor(permission.project) ?? 'cccccc')}
          ticMeta={permission.project.tic}
        />
        <div className={styles.names}>
          <Box.Column>
            <div className={classNames(styles.name, 'fs-mask')}>{permission.project.name}</div>
            <div className={classNames(styles.clientName, 'fs-mask')}>
              {permission.project.client.name}
            </div>
          </Box.Column>
          {assignedTeams.length > 0 && (
            <NewTooltip
              tooltip={`Added via ${pluralize(assignedTeams.length, {
                singular: 'team',
                plural: 'teams',
              })}: ${assignedTeams.map(t => t.name).join(', ')}`}
            >
              <Pill>
                {pluralize(assignedTeams.length, {
                  singular: 'team',
                  plural: 'teams',
                })}
              </Pill>
            </NewTooltip>
          )}
        </div>
        <div className={styles.rate}>
          <>
            {permission.rateType === RATE_TYPES.USER_RATE && this.renderUserRate()}
            {permission.rateType === RATE_TYPES.PROJECT_RATE && this.renderFixedRate()}

            {permission.rateType === RATE_TYPES.NON_BILLABLE && <p>Non-billable</p>}
          </>
        </div>
        <Tooltip position="left" title="Remove person from this project">
          <Button size="small" priority="low" onClick={this.handleRemove} tabIndex={-1}>
            <FontAwesome name="times" />
          </Button>
        </Tooltip>
      </div>
    );
  }
}

export default UserEditorProject;
